<script setup>
import { ref, onMounted, reactive, watch } from "vue";
import { apiQuery } from "@/api/j24api";
import AlertsList from "@/components/AlertsList.vue";
import { permissionsStore } from "@/stores/permissions";
import VueDatePicker from "@vuepic/vue-datepicker";

const permissions = permissionsStore();

const frequencyOptions = [
  { title: "Daily", value: "daily" },
  { title: "Weekly", value: "weekly" },
  { title: "Monthly", value: "monthly" },
  { title: "Quarterly", value: "quarterly" },
  { title: "Yearly", value: "yearly" },
];

const headers = [
  { text: "MDM Server", value: "mdmServerName" },
  { text: "Title", value: "title" },
  { text: "Frequency", value: "frequency" },
  { text: "Last Run", value: "lastRun" },
  { text: "Last Reference", value: "lastReference" },
  { text: "Actions", value: "actions" },
];

const templateHeaders = [
  { text: "Title", value: "title" },
  { text: "Frequency", value: "frequency" },
  { text: "Actions", value: "actions" },
];

const templateSearchFields = ["title", "frequency"];

const templates = reactive([]);
const snowTemplates = reactive([]);
const mdmServers = reactive([]);
const roadmapItems = reactive([]);
const loading = ref(true);
const alerts = reactive([]);
const hasPermission = ref(false);

onMounted(async () => {
  console.log("Permissions", permissions.$state);
  hasPermission.value = permissions.$state["Dashboard-Roadmap"];
  await getMDMServers();
  getData();
});

async function getData() {
  loading.value = true;
  const itemsResult = await apiQuery("ELA", "dashboard/roadmap", {
    apiAction: "getItems",
  });
  const templatesResult = await apiQuery("ELA", "dashboard/roadmap", {
    apiAction: "getTemplates",
  });
  if (templatesResult.templates) {
    templates.length = 0;
    templates.push(...templatesResult.templates);
  }
  if (templatesResult.snowTemplates) {
    snowTemplates.length = 0;
    snowTemplates.push(...templatesResult.snowTemplates);
  }
  if (itemsResult.length) {
    itemsResult.forEach((item) => {
      if (item.mdmServer) {
        const server = mdmServers.find((s) => s._id == item.mdmServer);
        item.mdmServerName = server ? server.name : "Unknown Server";
      }
    });
    roadmapItems.length = 0;
    roadmapItems.push(...itemsResult);
  }
  loading.value = false;
}

async function getMDMServers() {
  if (mdmServers.length == 0) {
    var result = await apiQuery("J24Auth", "elaadmin/mdmservers", {
      apiAction: "get",
    });
    if (result.length) {
      // sort by name

      result.sort((a, b) => a.name.localeCompare(b.name));
      // Filter to only those with accountNo set
      result = result.filter((server) => server.accountNo);
      mdmServers.push(...result);
    }
  }
}

// Add Template
const addTemplateDialog = ref(false);
const addTemplate = ref({});
const addTemplateLoading = ref(false);

async function addTemplateClicked() {
  addTemplateLoading.value = true;
  if (editTemplate.value) {
    await apiQuery("ELA", "dashboard/roadmap", {
      apiAction: "editTemplate",
      data: addTemplate.value,
    });
  } else {
    await apiQuery("ELA", "dashboard/roadmap", {
      apiAction: "addTemplate",
      data: addTemplate.value,
    });
  }
  addTemplateLoading.value = false;
  addTemplateDialog.value = false;
  getData();
}

// Add Roadmap Item
const addRoadmapItemDialog = ref(false);
const addRoadmapItem = ref({});
const selectedTemplate = ref("");
const addRoadmapItemLoading = ref(false);
const editRoadmapItem = ref(false);

async function addRoadmapItemClicked() {
  console.log("Add Roadmap Item");
  addRoadmapItemLoading.value = true;
  if (editRoadmapItem.value) {
    let result = await apiQuery("ELA", "dashboard/roadmap", {
      apiAction: "updateItem",
      data: addRoadmapItem.value,
    });
  } else {
    var validationCheck = true;
    if (!addRoadmapItem.value.mdmServer) {
      alerts.push({
        type: "error",
        message: "Please select MDM Server",
        closable: true,
      });
      validationCheck = false;
    }
    if (!addRoadmapItem.value.title) {
      alerts.push({
        type: "error",
        message: "Please enter Title",
        closable: true,
      });
      validationCheck = false;
    }
    if (!addRoadmapItem.value.frequency) {
      alerts.push({
        type: "error",
        message: "Please select Frequency",
        closable: true,
      });
      validationCheck = false;
    }
    if (!addRoadmapItem.value.snowTemplate) {
      alerts.push({
        type: "error",
        message: "Please select Service Now Template",
        closable: true,
      });
      validationCheck = false;
    }
    if (validationCheck) {
      addRoadmapItem.value.mdmServer.forEach((server) => {
        let matchingItems = roadmapItems.filter(
          (item) =>
            item.mdmServer == server && item.title == addRoadmapItem.value.title
        );
        if (matchingItems.length) {
          let serverName = mdmServers.find((s) => s._id == server).name;
          alerts.push({
            type: "error",
            message: `Item with title ${addRoadmapItem.value.title} already exists for MDM Server ${serverName}`,
            closable: true,
          });
          validationCheck = false;
        }
      });
    }
    if (!validationCheck) {
      addRoadmapItemLoading.value = false;
      return;
    }

    // if mdmServer is a string convert to array
    if (!Array.isArray(addRoadmapItem.value.mdmServer)) {
      console.log("Converting to array");
      addRoadmapItem.value.mdmServer = [addRoadmapItem.value.mdmServer];
    }

    let result = await apiQuery("ELA", "dashboard/roadmap", {
      apiAction: "addItem",
      data: addRoadmapItem.value,
    });
  }
  addRoadmapItemLoading.value = false;
  addRoadmapItemDialog.value = false;
  editRoadmapItem.value = false;
  addRoadmapItem.value = {};
  getData();
}

watch(selectedTemplate, (newVal) => {
  console.log("Selected Template", newVal);
  if (newVal) {
    const template = templates.find((t) => t._id == newVal);
    if (template) {
      addRoadmapItem.value.title = template.title;
      addRoadmapItem.value.description = template.description;
      addRoadmapItem.value.frequency = template.frequency;
      addRoadmapItem.value.snowTemplate = template.snowTemplate;
    }
  }
});

const searchValue = ref("");
const searchFields = ref(["mdmServerName", "title", "frequency"]);
const selectedTab = ref("items");
const deleteItem = ref({});
const deleteItemLoading = ref(false);
const confirmDeleteItem = ref(false);
async function deleteItemConfirmed() {
  console.log("Delete Item", deleteItem.value);
  deleteItemLoading.value = true;
  await apiQuery("ELA", "dashboard/roadmap", {
    apiAction: "updateItem",
    data: { _id: deleteItem.value._id, deleted: true },
  });
  deleteItemLoading.value = false;
  confirmDeleteItem.value = false;
  deleteItem.value = {};
  getData();
}

const editTemplate = ref(false);
const deleteTemplate = ref({});
const confirmDeleteTemplate = ref(false);
async function deleteTemplateConfirmed() {
  console.log("Delete Template", deleteTemplate.value);
  deleteItemLoading.value = true;
  await apiQuery("ELA", "dashboard/roadmap", {
    apiAction: "editTemplate",
    data: { _id: deleteTemplate.value._id, deleted: true },
  });
  deleteItemLoading.value = false;
  confirmDeleteTemplate.value = false;
  deleteTemplate.value = {};
  getData();
}
</script>

<template>
  <v-dialog v-model="confirmDeleteItem" max-width="400">
    <v-card>
      <v-card-title>Confirm Delete</v-card-title>
      <v-card-text> Are you sure you want to delete this item?</v-card-text>
      <v-card-actions>
        <v-btn @click="confirmDeleteItem = false">Cancel</v-btn>
        <v-btn
          @click="deleteItemConfirmed()"
          color="red"
          :loading="deleteItemLoading"
          >Delete</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="confirmDeleteTemplate" max-width="400">
    <v-card>
      <v-card-title>Confirm Delete</v-card-title>
      <v-card-text> Are you sure you want to delete this template?</v-card-text>
      <v-card-actions>
        <v-btn @click="confirmDeleteTemplate = false">Cancel</v-btn>
        <v-btn
          @click="deleteTemplateConfirmed()"
          color="red"
          :loading="deleteItemLoading"
          >Delete</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="addRoadmapItemDialog" max-width="500">
    <v-card>
      <v-card-title>{{
        editRoadmapItem ? "Add New Roadmap Item" : "Edit Roadmap Item"
      }}</v-card-title>
      <v-card-text>
        <v-row v-if="alerts">
          <v-col>
            <AlertsList :modelValue="alerts" />
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="!editRoadmapItem">
            <v-select
              v-model="addRoadmapItem.mdmServer"
              :items="mdmServers"
              item-title="name"
              item-value="_id"
              label="MDM Server"
              multiple
            ></v-select>
          </v-col>
          <v-col v-else>
            <v-text-field
              v-model="addRoadmapItem.mdmServerName"
              label="MDM Server"
              readonly
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-select
              v-model="selectedTemplate"
              :items="templates"
              item-title="title"
              item-value="_id"
              label="Choose Template or custom"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="addRoadmapItem.title"
              label="Title"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea
              v-model="addRoadmapItem.description"
              label="Description"
            ></v-textarea>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-select
              v-model="addRoadmapItem.frequency"
              :items="frequencyOptions"
              label="Frequency"
              hide-details
            ></v-select>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            Last Run
            <VueDatePicker
              v-model="addRoadmapItem.lastRun"
              label="Last Run"
              placeholder="Select Date"
              auto-apply
              :enable-time-picker="false"
              :month-change-on-scroll="false"
            />
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-select
              v-model="addRoadmapItem.snowTemplate"
              :items="snowTemplates"
              item-title="name"
              item-value="_id"
              label="Service Now Template"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="auto">
            <v-btn
              @click="
                addRoadmapItemDialog = false;
                addRoadmapItem = {};
                selectedTemplate = '';
              "
              >Cancel</v-btn
            >
          </v-col>
          <v-col cols="auto">
            <v-btn
              @click="addRoadmapItemClicked"
              :loading="addRoadmapItemLoading"
              >Save</v-btn
            >
          </v-col>
          <v-spacer></v-spacer>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>

  <v-dialog v-model="addTemplateDialog" max-width="500">
    <v-card>
      <v-card-title>Add New Template</v-card-title>
      <v-card-text>
        <v-row>
          <v-col>
            <v-text-field
              v-model="addTemplate.title"
              label="Title"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-textarea
              v-model="addTemplate.description"
              label="Description"
            ></v-textarea>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-select
              v-model="addTemplate.frequency"
              :items="frequencyOptions"
              label="Frequency"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-select
              v-model="addTemplate.snowTemplate"
              :items="snowTemplates"
              item-title="name"
              item-value="_id"
              label="Service Now Template"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="auto">
            <v-btn @click="addTemplateDialog = false">Cancel</v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn @click="addTemplateClicked" :loading="addTemplateLoading"
              >Save</v-btn
            >
          </v-col>
          <v-spacer></v-spacer>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>

  <v-row v-if="hasPermission">
    <v-col cols="12" class="ma-2">
      <v-card>
        <v-card-text>
          <v-window v-model="selectedTab">
            <v-window-item value="items">
              <v-row>
                <v-col cols="auto">
                  <v-tabs v-model="selectedTab" color="#e7541e" class="mb-2">
                    <v-tab value="items">Roadmap Items</v-tab>
                    <v-tab value="templates">Templates</v-tab>
                  </v-tabs>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto">
                  <v-btn @click="addRoadmapItemDialog = true"
                    >Add Roadmap Item</v-btn
                  >
                </v-col>
              </v-row>

              <EasyDataTable
                :headers="headers"
                :items="roadmapItems"
                theme-color="#e7541e"
                alternating
                buttons-pagination
                :loading="loading"
                :search-value="searchValue"
                :search-fields="searchFields"
              >
                <template #item-actions="item">
                  <v-btn
                    @click="
                      addRoadmapItemDialog = true;
                      editRoadmapItem = true;
                      addRoadmapItem = item;
                    "
                    icon="mdi-pencil"
                    size="small"
                    :rounded="0"
                    variant="text"
                  ></v-btn>
                  <v-btn
                    @click="
                      deleteItem = item;
                      confirmDeleteItem = true;
                    "
                    icon="mdi-delete"
                    size="small"
                    :rounded="0"
                    variant="text"
                  ></v-btn>
                </template>
              </EasyDataTable>
            </v-window-item>
            <v-window-item value="templates">
              <v-row>
                <v-col cols="auto">
                  <v-tabs v-model="selectedTab" color="#e7541e" class="mb-2">
                    <v-tab value="items">Roadmap Items</v-tab>
                    <v-tab value="templates">Templates</v-tab>
                  </v-tabs>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto">
                  <v-btn @click="addTemplateDialog = true">Add Template</v-btn>
                </v-col>
              </v-row>

              <EasyDataTable
                :headers="templateHeaders"
                :items="templates"
                theme-color="#e7541e"
                alternating
                buttons-pagination
                :loading="loading"
                :search-value="searchValue"
                :search-fields="templateSearchFields"
              >
                <template #item-actions="item">
                  <v-btn
                    @click="
                      editTemplate = true;
                      addTemplateDialog = true;
                      addTemplate = item;
                    "
                    icon="mdi-pencil"
                    size="small"
                    :rounded="0"
                    variant="text"
                  ></v-btn>
                  <v-btn
                    @click="
                      deleteTemplate = item;
                      confirmDeleteTemplate = true;
                    "
                    icon="mdi-delete"
                    size="small"
                    :rounded="0"
                    variant="text"
                  ></v-btn>
                </template>
              </EasyDataTable>
            </v-window-item>
          </v-window>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
  <v-row v-else>
    <v-col class="pa-5">
      <v-alert type="error" icon="mdi-cancel">
        You do not have permission to view this page
      </v-alert>
    </v-col>
  </v-row>
</template>
